import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { processNaturalLanguageQuery } from '../utils/openai';
import { Bot, User, Trash2, Send, Search, Clock } from 'lucide-react';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const AIChat = ({ reviews }) => {
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: "Hi! I'm Clairity, your AI assistant for analyzing Camelback Resort reviews. You can ask me questions like:\n\n• What are guests saying about Aquatopia in December?\n• Show me all negative reviews about ski conditions\n• What are the main issues with hotel service?\n• How do ratings compare between segments?\n\nWhat would you like to know?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;
    
    if (reviews.length === 0) {
      setMessages(prev => [...prev, 
        {
          type: 'user',
          content: inputValue,
          timestamp: new Date()
        },
        {
          type: 'ai',
          content: "I don't have any review data to analyze right now. Please make sure reviews are loaded first so I can help you!",
          timestamp: new Date()
        }
      ]);
      setInputValue('');
      return;
    }

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      // Add review context to the AI processing
      const reviewContext = {
        totalReviews: reviews.length,
        dateRange: getDateRange(reviews),
        segments: getSegmentBreakdown(reviews)
      };

      const response = await processNaturalLanguageQuery(inputValue, reviews, reviewContext);
      
      const aiMessage = {
        type: 'ai',
        content: response,
        timestamp: new Date(),
        isMarkdown: true
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        type: 'ai',
        content: `Sorry, I encountered an error: ${error.message}. Please try rephrasing your question and I'll do my best to help!`,
        timestamp: new Date(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (reviewData) => {
    if (reviewData.length === 0) return null;
    const dates = reviewData.map(r => new Date(r.DATE)).sort();
    const earliest = dates[0];
    const latest = dates[dates.length - 1];
    return {
      earliest: earliest.toLocaleDateString(),
      latest: latest.toLocaleDateString()
    };
  };

  const getSegmentBreakdown = (reviewData) => {
    const segments = {};
    reviewData.forEach(review => {
      segments[review.SEGMENT] = (segments[review.SEGMENT] || 0) + 1;
    });
    return segments;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        type: 'ai',
        content: "Chat cleared! I'm ready to help analyze the reviews. What would you like to know?",
        timestamp: new Date()
      }
    ]);
  };

  const allSuggestedQuestions = [
    "What are the most common complaints by segment?",
    "How do ratings trend over time?", 
    "What do guests love most about each area?",
    "Are there any seasonal patterns in the reviews?",
    "What are the main issues guests mention?",
    "Which staff members are mentioned most positively?",
    "What complaints come up most in 1-star reviews?",
    "How satisfied are guests with room cleanliness?",
    "What do guests say about wait times?",
    "Which amenities get the best feedback?",
    "Are there any accessibility concerns mentioned?",
    "What do families with kids enjoy most?",
    "How do weekend vs weekday experiences differ?",
    "What seasonal activities get the best reviews?",
    "Are there any recurring maintenance issues?"
  ];

  // Get 4 random suggestions that rotate each session
  const getRandomSuggestions = () => {
    const shuffled = [...allSuggestedQuestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  };

  const [suggestedQuestions] = useState(() => getRandomSuggestions());

  const handleSuggestedQuestion = (question) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const renderMessageContent = (content, isMarkdown = false) => {
    if (isMarkdown) {
      return (
        <ReactMarkdown
          components={{
            p: ({ children }) => <p style={{ margin: '0.5rem 0' }}>{children}</p>,
            ul: ({ children }) => <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>{children}</ul>,
            ol: ({ children }) => <ol style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>{children}</ol>,
            li: ({ children }) => <li style={{ margin: '0.25rem 0' }}>{children}</li>,
            strong: ({ children }) => <strong style={{ color: '#1e293b', fontWeight: '600' }}>{children}</strong>,
            em: ({ children }) => <em style={{ color: '#6b7280', fontStyle: 'italic' }}>{children}</em>,
            code: ({ children }) => (
              <code style={{ 
                backgroundColor: '#f1f5f9', 
                padding: '0.125rem 0.25rem', 
                borderRadius: '0.25rem',
                fontSize: '0.875rem',
                fontFamily: 'monospace'
              }}>{children}</code>
            )
          }}
        >
          {content}
        </ReactMarkdown>
      );
    }

    // Fallback for non-markdown content
    const parts = content.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return <strong key={index}>{boldText}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="ai-chat">
      <div className="chat-header">
        <div className="header-info">
          <h3><Bot size={20} /> Clairity</h3>
          <p>Ask questions about {formatNumber(reviews.length)} reviews</p>
          {reviews.length < 100 && (
            <p className="filter-notice"><Search size={14} /> Filtered data - adjust filters above to change scope</p>
          )}
        </div>
        <button className="clear-chat-btn" onClick={clearChat}>
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="chat-messages">
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.type}`}>
            <div className="message-avatar">
              {message.type === 'ai' ? <Bot size={16} /> : <User size={16} />}
            </div>
            <div className="message-content">
              <div className={`message-bubble ${message.isError ? 'error' : ''}`}>
                {message.isMarkdown ? (
                  renderMessageContent(message.content, true)
                ) : (
                  message.content.split('\n').map((line, lineIndex) => (
                    <p key={lineIndex}>{renderMessageContent(line)}</p>
                  ))
                )}
              </div>
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="message ai">
            <div className="message-avatar"><Bot size={16} /></div>
            <div className="message-content">
              <div className="message-bubble loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                Analyzing reviews...
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="suggested-questions">
          <h4>Try asking:</h4>
          <div className="suggestions-grid">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                className="suggestion-btn"
                onClick={() => handleSuggestedQuestion(question)}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-input-container">
        <div className="chat-input">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Clairity anything about the reviews..."
            disabled={loading}
            rows={1}
          />
          <button 
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || loading}
            className="send-btn"
          >
            {loading ? <Clock size={16} /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;